import { styled } from '@linaria/react';
import { Stack } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAgentSessions } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import type { AgentMinerSession } from '../../../api/agent-miner-client/agent-miner-client.type';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { Table } from '../../../components/table/table';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { inkDarkest, inkLight } from '../../../styles/design-tokens';
import { DEFAULT_PAGE_SIZE } from '../agent-miner.constants';
import { useShowAgentMinerApiCost } from '../shared/agent-miner.hooks';
import type { SessionsFiltersValue } from './filters/session-filters.type';
import { buildListParams } from './filters/sessions-filters.hooks';
import { getSessionsListColumns } from './sessions-list.columns';

type SessionsListProps = {
  filterValue: SessionsFiltersValue;
  onSelectSession: (sessionId: string | null) => void;
  selectedSessionId: string | null;
};

export function SessionsList({ filterValue, onSelectSession, selectedSessionId }: SessionsListProps) {
  const { t } = useTranslation();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const hideApiCost = !useShowAgentMinerApiCost();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const pageSize = Math.max(
    1,
    Number.parseInt(searchParams.get('page_size') ?? String(DEFAULT_PAGE_SIZE), 10) || DEFAULT_PAGE_SIZE,
  );
  const requestedOrdering = searchParams.get('ordering') ?? '-last_turn_at';
  // When cost is hidden the column header is gone, so a stale `ordering=total_cost_usd`
  // URL would silently sort by an invisible, unchangeable field. Fall back to default.
  const ordering =
    hideApiCost && requestedOrdering.replace(/^-/, '') === 'total_cost_usd' ? '-last_turn_at' : requestedOrdering;

  const listParams = useMemo(
    () =>
      buildListParams(filterValue, {
        organization_id: organizationId ?? '',
        page,
        page_size: pageSize,
        ordering,
      }),
    [filterValue, organizationId, page, pageSize, ordering],
  );

  const { data, query } = useAgentSessions(listParams);

  const totalCount = data?.count ?? 0;
  const rows: AgentMinerSession[] = data?.results ?? [];

  // The Related PRs column only renders for orgs with an active SCM integration
  // (the backend gate); otherwise the column would be dead weight for every row.
  const showRelatedPrs = data?.scm_integration_active ?? false;
  const sessionsListColumns = useMemo(
    () => getSessionsListColumns(t, hideApiCost, showRelatedPrs),
    [t, hideApiCost, showRelatedPrs],
  );

  const setPage = (nextPage: number) => {
    const copy = new URLSearchParams(searchParams);
    copy.set('page', String(nextPage));
    setSearchParams(copy, { replace: true });
  };

  // Clamp back to page 1 when a stale `page=N` returns no rows but the server
  // has matches on earlier pages — prevents a misleading empty-state while
  // data exists on other pages.
  useEffect(() => {
    if (!query.isLoading && rows.length === 0 && totalCount > 0 && page > 1) {
      const copy = new URLSearchParams(searchParams);
      copy.set('page', '1');
      setSearchParams(copy, { replace: true });
    }
  }, [query.isLoading, rows.length, totalCount, page, searchParams, setSearchParams]);

  const handleSortChange = (sortBy: keyof AgentMinerSession, direction: 'asc' | 'desc') => {
    const column = String(sortBy);
    const field =
      column === 'turns_count' ||
      column === 'total_cost_usd' ||
      column === 'started_at' ||
      column === 'last_turn_at' ||
      column === 'duration_ms'
        ? column
        : 'last_turn_at';
    const copy = new URLSearchParams(searchParams);
    copy.set('ordering', direction === 'desc' ? `-${field}` : field);
    copy.set('page', '1');
    setSearchParams(copy, { replace: true });
  };

  return (
    <Container>
      <Stack style={{ flex: 1, minHeight: 0, position: 'relative' }} gap={8}>
        <BrandedLoadingOverlay visible={query.isLoading} variant="colored" />
        <TableWrapper>
          {!query.isLoading && totalCount === 0 ? (
            <EmptyState>
              <div className="title">No sessions match your filters</div>
              <div className="subtitle">Try clearing a filter or broadening your search.</div>
            </EmptyState>
          ) : (
            <Table<AgentMinerSession>
              data={rows}
              columns={sessionsListColumns}
              getRowId={(row) => row.id}
              onRowClick={(row) => onSelectSession(row.id)}
              selectedRowId={selectedSessionId ?? undefined}
              pageSize={pageSize}
              currentPage={page}
              totalItems={totalCount}
              onPageChange={setPage}
              serverSidePagination
              onSortChange={handleSortChange}
              stickyHeader
            />
          )}
        </TableWrapper>
      </Stack>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  height: 100%;
  position: relative;
`;

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div`
  padding: 60px;
  text-align: center;

  > .title {
    font: 600 14px/1.3 'Figtree', sans-serif;
    color: ${inkDarkest};
    margin-bottom: 6px;
  }

  > .subtitle {
    font: 400 12.5px/1.4 'Figtree', sans-serif;
    color: ${inkLight};
  }
`;
